import Cookies from 'js-cookie';

import { nothing } from '../../utils/yambda';

interface INothingStorage {
  get: () => void;
  set: () => void;
  remove: () => void;
}
const emptyStorage: INothingStorage = {
  get: nothing,
  set: nothing,
  remove: nothing,
};

export class BrowserStorage {
  private storage: Storage;

  private constructor(storage: Storage) {
    this.storage = storage;
  }

  static create(type: 'local' | 'session'): BrowserStorage {
    if (type === 'local') {
      return new this(window.localStorage);
    }
    return new this(window.sessionStorage);
  }

  get<T = any>(key: string, defaultValue?: T): T | null {
    const value = this.storage.getItem(key);

    if (!value) {
      return defaultValue !== undefined ? defaultValue : null;
    }

    try {
      return JSON.parse(value) as T;
    } catch {
      return (value || defaultValue) as T;
    }
  }

  set(key: string, value: any): void {
    if (typeof value === 'object') {
      this.storage.setItem(key, JSON.stringify(value));
    } else {
      this.storage.setItem(key, value);
    }
  }

  remove(key: string): void {
    this.storage.removeItem(key);
  }
}

class BrowserCookies {
  private storage: Cookies.CookiesStatic;

  private constructor(storage: Cookies.CookiesStatic) {
    this.storage = storage;
  }

  static create(): BrowserCookies {
    return new this(Cookies);
  }

  get(key?: string, defaultValue?: any): any {
    // js-cookie: 매개변수 없으면 모든 쿠키 회수
    const value = key ? this.storage.get(key) : this.storage.get();

    if (!value) {
      return defaultValue !== undefined ? defaultValue : value;
    }

    // js-cookie: 모든 쿠키인 경우 이미 객체이므로 그대로 보내줌
    if (typeof value === 'object') {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      return value || defaultValue;
    }
  }

  set(key: string, value: any, options?: any): void {
    if (typeof value === 'object') {
      this.storage.set(key, JSON.stringify(value), options);
    } else {
      this.storage.set(key, value, options);
    }
  }

  remove(key: string, options?: Cookies.CookieAttributes): void {
    this.storage.remove(key, options);
  }
}

export default class StoreService {
  static getInstance(type?: 'local' | 'session'): BrowserStorage; // #get()에 key 필수
  static getInstance(type: 'cookie'): BrowserCookies; // #get()에 key 없으면 쿠키 전체 반환
  /**
   * @param type 기본값 `local`
   */
  static getInstance(
    type: 'local' | 'session' | 'cookie' = 'local',
  ): BrowserStorage | BrowserCookies | INothingStorage {
    if (!process.browser) {
      return emptyStorage;
    }

    if (type === 'session' || type === 'local') {
      return BrowserStorage.create(type);
    }

    return BrowserCookies.create();
  }
}
